import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useGame } from '../../context/GameContext';
import { resources, buildings } from '../../data';
import { WONDERS, WONDER_BALANCE, getWondersByTier, getWonderTiers, formatWonderTime, getEraName } from '../../data/wonders';
import { GREAT_WORKS, GREAT_WORKS_BALANCE, getGreatWorksByTier, getGreatWorkTiers } from '../../data/world-map/greatWorks';
import { getRequirementSummary, canAffordGreatWork, findValidPlacements } from '../../data/world-map/greatWorksUtils';
import { isWonderSiteBound } from '../../data/world-map/wonderSites';
import { ResourceChip, BuildingChip } from '../common';
import './MonumentsPanel.css';

function MonumentsPanel({ onNotification, onSelectGreatWork }) {
  const {
    playerResources,
    buildingCounts,
    builtWonders,
    currentWonderConstruction,
    startWonderConstruction,
    cancelWonderConstruction,
    destroyWonder,
    getWonderProgressInfo,
    canBuildWonder,
    // Great Works
    builtGreatWorks,
    greatWorkLocations,
    currentGreatWorkConstruction,
    startGreatWorkConstruction,
    cancelGreatWorkConstruction,
    destroyGreatWork,
    getGreatWorkProgressInfo,
    worldMap,
  } = useGame();

  const [activeTab, setActiveTab] = useState('wonders'); // 'wonders' or 'greatWorks'
  const [selectedTier, setSelectedTier] = useState(1);
  const [selectedGWTier, setSelectedGWTier] = useState(1);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [showConfirmDestroy, setShowConfirmDestroy] = useState(null);
  const [showGWConfirmCancel, setShowGWConfirmCancel] = useState(false);
  const [showGWConfirmDestroy, setShowGWConfirmDestroy] = useState(null);

  // Track recently completed wonders for celebration animation
  const [justCompletedWonder, setJustCompletedWonder] = useState(null);
  const prevBuiltWondersRef = React.useRef(builtWonders || []);

  // PERF: Local timer to force re-render for progress display
  // This is needed because progress is now calculated from timestamps, not state
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    // Only run timer if something is being constructed
    if (!currentWonderConstruction && !currentGreatWorkConstruction) return;

    const interval = setInterval(() => {
      forceUpdate(n => n + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [currentWonderConstruction, currentGreatWorkConstruction]);

  // Detect wonder completion and trigger celebration animation
  useEffect(() => {
    const prevWonders = prevBuiltWondersRef.current || [];
    const currentWonders = builtWonders || [];

    // Find newly completed wonders
    const newlyCompleted = currentWonders.filter(w => !prevWonders.includes(w));

    if (newlyCompleted.length > 0) {
      // Trigger celebration for the first newly completed wonder
      setJustCompletedWonder(newlyCompleted[0]);
      // Clear celebration after animation (0.8s animation duration)
      const timer = setTimeout(() => {
        setJustCompletedWonder(null);
      }, 800);
      prevBuiltWondersRef.current = currentWonders;
      return () => clearTimeout(timer);
    }

    prevBuiltWondersRef.current = currentWonders;
  }, [builtWonders]);

  const progressInfo = getWonderProgressInfo?.() || null;
  const gwProgressInfo = getGreatWorkProgressInfo?.() || null;
  const tierWonders = useMemo(() => getWondersByTier(selectedTier), [selectedTier]);
  const tiers = useMemo(() => getWonderTiers(), []);

  // Wonder Sites - check if site-bound wonders have been discovered on the map
  const discoveredWonderSites = worldMap?.discoveredWonderSites || [];

  // Great Works memoized values
  const gwTierWorks = useMemo(() => getGreatWorksByTier(selectedGWTier), [selectedGWTier]);
  const gwTiers = useMemo(() => getGreatWorkTiers(), []);
  const discoveredNodes = worldMap?.discoveredNodes || {};
  const builtWorkNodeIds = useMemo(
    () => new Set(Object.values(greatWorkLocations || {})),
    [greatWorkLocations]
  );

  // Pre-calculate valid placements for all works in the current tier
  const validPlacementsMap = useMemo(() => {
    const map = {};
    gwTierWorks.forEach(work => {
      if (!builtGreatWorks?.includes(work.id) && currentGreatWorkConstruction !== work.id) {
        map[work.id] = findValidPlacements(work.id, discoveredNodes, builtWorkNodeIds);
      } else {
        map[work.id] = [];
      }
    });
    return map;
  }, [gwTierWorks, builtGreatWorks, currentGreatWorkConstruction, discoveredNodes, builtWorkNodeIds]);

  const wondersBuilt = builtWonders.length;
  const wondersRemaining = WONDER_BALANCE.maxTotal - wondersBuilt;
  const greatWorksBuilt = builtGreatWorks?.length || 0;
  const greatWorksRemaining = GREAT_WORKS_BALANCE.maxTotal - greatWorksBuilt;

  const handleStartConstruction = (wonderId) => {
    const result = startWonderConstruction(wonderId);
    if (result.success) {
      const wonder = WONDERS[wonderId];
      onNotification?.(`🏛️ Started building ${wonder.name}!`, 'success');
    } else {
      const messages = {
        already_building: 'Already constructing a wonder!',
        max_wonders_reached: 'Maximum wonders reached! Destroy one first.',
        already_built: 'This wonder is already built!',
        insufficient_resources: 'Not enough resources!',
        insufficient_buildings: 'Not enough buildings!',
        insufficient_both: 'Need more resources and buildings!',
      };
      onNotification?.(messages[result.reason] || 'Cannot build wonder', 'error');
    }
  };

  const handleCancel = () => {
    if (cancelWonderConstruction()) {
      onNotification?.('Construction cancelled. 50% resources refunded.', 'warning');
      setShowConfirmCancel(false);
    }
  };

  const handleDestroy = (wonderId) => {
    const wonder = WONDERS[wonderId];
    const result = destroyWonder?.(wonderId);
    if (result?.success) {
      onNotification?.(`💥 ${wonder.name} has been demolished.`, 'warning');
      setShowConfirmDestroy(null);
    }
  };

  // Great Works handlers
  const handleSelectGreatWork = useCallback((workId) => {
    const work = GREAT_WORKS[workId];
    const affordCheck = canAffordGreatWork(workId, playerResources);

    if (!affordCheck.canAfford) {
      onNotification?.('Not enough resources for this Great Work!', 'error');
      return;
    }

    if (currentGreatWorkConstruction) {
      onNotification?.('Already constructing a Great Work!', 'error');
      return;
    }

    if (greatWorksRemaining <= 0) {
      onNotification?.('Maximum Great Works reached! Destroy one first.', 'error');
      return;
    }

    // Find valid placements
    const validPlacements = findValidPlacements(workId, discoveredNodes, builtWorkNodeIds);

    if (validPlacements.length === 0) {
      onNotification?.(`No valid map locations for ${work.name}. Explore more tiles!`, 'warning');
      return;
    }

    // Notify parent to show placement mode on map
    onSelectGreatWork?.(workId, validPlacements);
    onNotification?.(`Select a tile on the map to place ${work.name}`, 'info');
  }, [playerResources, currentGreatWorkConstruction, greatWorksRemaining, discoveredNodes, builtWorkNodeIds, onSelectGreatWork, onNotification]);

  const handleGWCancel = () => {
    const result = cancelGreatWorkConstruction?.();
    if (result?.success) {
      onNotification?.('Great Work construction cancelled. 50% resources refunded.', 'warning');
      setShowGWConfirmCancel(false);
    }
  };

  const handleGWDestroy = (workId) => {
    const work = GREAT_WORKS[workId];
    const result = destroyGreatWork?.(workId);
    if (result?.success) {
      onNotification?.(`💥 ${work.name} has been demolished.`, 'warning');
      setShowGWConfirmDestroy(null);
    }
  };

  // Format time for display
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  return (
    <div className="wonder-panel">
      {/* Tab Toggle */}
      <div className="wp-tabs">
        <button
          className={`wp-tab ${activeTab === 'wonders' ? 'active' : ''}`}
          onClick={() => setActiveTab('wonders')}
        >
          <span className="wp-tab-icon">🏛️</span>
          <span className="wp-tab-label">Wonders</span>
          <span className="wp-tab-count">{wondersBuilt}/{WONDER_BALANCE.maxTotal}</span>
        </button>
        <button
          className={`wp-tab ${activeTab === 'greatWorks' ? 'active' : ''}`}
          onClick={() => setActiveTab('greatWorks')}
        >
          <span className="wp-tab-icon">🗿</span>
          <span className="wp-tab-label">Great Works</span>
          <span className="wp-tab-count">{greatWorksBuilt}/{GREAT_WORKS_BALANCE.maxTotal}</span>
        </button>
      </div>

      {/* === WONDERS TAB === */}
      {activeTab === 'wonders' && (
        <>
          {/* System Info Header */}
          <div className="wp-header">
            <div className="wp-header-left">
              <h3>🏛️ Wonders</h3>
              <p className="wp-subtitle">Permanent monuments with powerful bonuses</p>
            </div>
            <div className="wp-slots">
              <div className="wp-slots-pips">
                {[...Array(WONDER_BALANCE.maxTotal)].map((_, i) => (
                  <div
                    key={i}
                    className={`wp-slot-pip ${i < wondersBuilt ? 'filled' : ''}`}
                    title={i < wondersBuilt ? WONDERS[builtWonders[i]]?.name : 'Empty'}
                  >
                    {i < wondersBuilt && WONDERS[builtWonders[i]]?.icon}
                  </div>
                ))}
              </div>
              <span className="wp-slots-text">{wondersBuilt}/{WONDER_BALANCE.maxTotal} Slots</span>
            </div>
          </div>

          {/* How It Works */}
          <div className="wp-info-bar">
            <div className="wp-info-item">
              <span className="wp-info-icon">🎯</span>
              <span>Max {WONDER_BALANCE.maxTotal} wonders total</span>
            </div>
            <div className="wp-info-item">
              <span className="wp-info-icon">⏳</span>
              <span>One at a time</span>
            </div>
            <div className="wp-info-item">
              <span className="wp-info-icon">🗑️</span>
              <span>Destroy to rebuild</span>
            </div>
            <div className="wp-info-item">
              <span className="wp-info-icon">💰</span>
              <span>Cancel = 50% refund</span>
            </div>
          </div>

      <div className="wp-content">
        {/* Active Construction */}
        {currentWonderConstruction && progressInfo && (
          <div className="wp-construction">
            <div className="wp-con-header">
              <span className="wp-con-icon">{progressInfo.wonder.icon}</span>
              <div className="wp-con-info">
                <span className="wp-con-name">{progressInfo.wonder.name}</span>
                <span className="wp-con-time">{progressInfo.formattedRemaining} remaining</span>
              </div>
              {WONDER_BALANCE.canCancel && !showConfirmCancel && (
                <button 
                  className="wp-con-cancel"
                  onClick={() => setShowConfirmCancel(true)}
                >
                  Cancel
                </button>
              )}
            </div>
            <div className="wp-con-bar">
              <div 
                className="wp-con-fill"
                style={{ width: `${progressInfo.percent}%` }}
              />
              <span className="wp-con-percent">{Math.floor(progressInfo.percent)}%</span>
            </div>
            {showConfirmCancel && (
              <div className="wp-con-confirm">
                <span>Cancel construction? 50% resources refunded.</span>
                <div className="wp-con-confirm-btns">
                  <button className="btn-confirm" onClick={handleCancel}>Yes, Cancel</button>
                  <button className="btn-deny" onClick={() => setShowConfirmCancel(false)}>No</button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Built Wonders */}
        {builtWonders.length > 0 && (
          <div className="wp-built">
            <div className="wp-built-header">
              <span>✨ Your Wonders</span>
            </div>
            <div className="wp-built-grid">
              {builtWonders.map(wonderId => {
                const wonder = WONDERS[wonderId];
                if (!wonder) return null;
                const isDestroying = showConfirmDestroy === wonderId;
                
                return (
                  <div key={wonderId} className={`wp-built-card ${isDestroying ? 'destroying' : ''}`}>
                    <div className="wp-built-main">
                      <span className="wp-built-icon">{wonder.icon}</span>
                      <div className="wp-built-info">
                        <span className="wp-built-name">{wonder.name}</span>
                        <div className="wp-built-effects">
                          {wonder.effects.productionBonus && Object.entries(wonder.effects.productionBonus).map(([res, mult]) => (
                            <span key={res} className="wp-effect-mini bonus">
                              {resources[res]?.icon}+{Math.round(mult * 100)}%
                            </span>
                          ))}
                          {wonder.effects.globalProductionBonus && (
                            <span className="wp-effect-mini global">
                              🌍+{Math.round(wonder.effects.globalProductionBonus * 100)}%
                            </span>
                          )}
                          {wonder.effects.researchSpeedBonus && (
                            <span className="wp-effect-mini research">
                              🔬+{Math.round(wonder.effects.researchSpeedBonus * 100)}%
                            </span>
                          )}
                          {wonder.effects.flatBonus && Object.entries(wonder.effects.flatBonus).map(([res, amt]) => (
                            <span key={res} className="wp-effect-mini flat">
                              {resources[res]?.icon}+{amt}/s
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    {isDestroying ? (
                      <div className="wp-built-destroy-confirm">
                        <span className="destroy-warn">⚠️ No refund!</span>
                        <button className="btn-destroy" onClick={() => handleDestroy(wonderId)}>Destroy</button>
                        <button className="btn-keep" onClick={() => setShowConfirmDestroy(null)}>Keep</button>
                      </div>
                    ) : (
                      <button 
                        className="wp-built-destroy"
                        onClick={() => setShowConfirmDestroy(wonderId)}
                        disabled={currentWonderConstruction !== null}
                        title="Destroy wonder"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {wondersRemaining === 0 && !currentWonderConstruction && (
              <div className="wp-max-warning">
                ⚠️ All slots full. Destroy a wonder to build another.
              </div>
            )}
          </div>
        )}

        {/* Tier Selector */}
        <div className="wp-tier-selector">
          {tiers.map(tier => {
            const tierList = getWondersByTier(tier);
            const builtInTier = tierList.filter(w => builtWonders.includes(w.id)).length;
            return (
              <button
                key={tier}
                className={`wp-tier-btn ${selectedTier === tier ? 'active' : ''}`}
                onClick={() => setSelectedTier(tier)}
              >
                <span className="wp-tier-icon">{WONDER_BALANCE.eraIcons?.[tier] || '🏛️'}</span>
                <span className="wp-tier-name">T{tier}</span>
                {builtInTier > 0 && <span className="wp-tier-check">✓</span>}
              </button>
            );
          })}
        </div>

        {/* Era Info */}
        <div className="wp-era-info">
          <span className="wp-era-icon">{WONDER_BALANCE.eraIcons?.[selectedTier] || '🏛️'}</span>
          <span className="wp-era-name">{getEraName(selectedTier)}</span>
          <span className="wp-era-desc">{WONDER_BALANCE.eraBonuses?.[selectedTier] || ''}</span>
        </div>

        {/* Wonder Cards Grid */}
        <div className="wp-grid" key={`wonders-tier-${selectedTier}`}>
          {tierWonders.map(wonder => {
            const isBuilt = builtWonders.includes(wonder.id);
            const isBuilding = currentWonderConstruction === wonder.id;
            const buildCheck = canBuildWonder?.(wonder.id) || { canBuild: false };

            // Check if this wonder requires a map site discovery
            const requiresSite = isWonderSiteBound(wonder.id);
            const siteDiscovered = requiresSite ? discoveredWonderSites.includes(wonder.id) : true;

            const resourceCosts = Object.entries(wonder.cost.resources).map(([res, amt]) => ({
              id: res,
              required: amt,
              have: playerResources[res] || 0,
              canAfford: (playerResources[res] || 0) >= amt,
            }));

            const buildingCosts = Object.entries(wonder.cost.buildings || {}).map(([bld, amt]) => ({
              id: bld,
              required: amt,
              have: buildingCounts[bld] || 0,
              canAfford: (buildingCounts[bld] || 0) >= amt,
            }));

            const allResourcesOk = resourceCosts.every(r => r.canAfford);
            const allBuildingsOk = buildingCosts.every(b => b.canAfford);
            const isJustCompleted = justCompletedWonder === wonder.id;

            return (
              <div
                key={wonder.id}
                className={`wp-card ${isBuilt ? 'built' : ''} ${isBuilding ? 'building' : ''} ${isJustCompleted ? 'just-completed' : ''}`}
              >
                {/* Card Header */}
                <div className="wp-card-header">
                  <span className="wp-card-icon">{wonder.icon}</span>
                  <div className="wp-card-title">
                    <span className="wp-card-name">{wonder.name}</span>
                    <span className="wp-card-time">⏱️ {formatWonderTime(wonder.buildTime)}</span>
                  </div>
                  {isBuilt && <span className="wp-card-badge built">✅ Built</span>}
                  {isBuilding && <span className="wp-card-badge building">⏳ Building</span>}
                </div>

                {/* Description */}
                <div className="wp-card-desc">{wonder.description}</div>

                {/* Site Discovery Status */}
                {requiresSite && !isBuilt && (
                  <div className={`wp-site-status ${siteDiscovered ? 'discovered' : 'undiscovered'}`}>
                    {siteDiscovered ? (
                      <>✓ Site Discovered</>
                    ) : (
                      <>🗺️ Discover Site on Map</>
                    )}
                  </div>
                )}

                {/* Effects Grid */}
                <div className="wp-card-section">
                  <div className="wp-card-section-label">Effects</div>
                  <div className="wp-card-effects">
                    {wonder.effects.productionBonus && Object.entries(wonder.effects.productionBonus).map(([res, mult]) => (
                      <div key={res} className="wp-effect bonus">
                        <span className="wp-effect-icon">{resources[res]?.icon || '📦'}</span>
                        <span className="wp-effect-text">+{Math.round(mult * 100)}% {resources[res]?.name}</span>
                      </div>
                    ))}
                    {wonder.effects.globalProductionBonus && (
                      <div className="wp-effect global">
                        <span className="wp-effect-icon">🌍</span>
                        <span className="wp-effect-text">+{Math.round(wonder.effects.globalProductionBonus * 100)}% ALL production</span>
                      </div>
                    )}
                    {wonder.effects.researchSpeedBonus && (
                      <div className="wp-effect research">
                        <span className="wp-effect-icon">🔬</span>
                        <span className="wp-effect-text">+{Math.round(wonder.effects.researchSpeedBonus * 100)}% research speed</span>
                      </div>
                    )}
                    {wonder.effects.flatBonus && Object.entries(wonder.effects.flatBonus).map(([res, amt]) => (
                      <div key={res} className="wp-effect flat">
                        <span className="wp-effect-icon">{resources[res]?.icon || '📦'}</span>
                        <span className="wp-effect-text">+{amt}/sec {resources[res]?.name}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cost Section (only if not built) */}
                {!isBuilt && (
                  <div className="wp-card-section">
                    <div className="wp-card-section-label">Cost</div>
                    
                    {/* Resource Costs */}
                    <div className="wp-card-costs">
                      {resourceCosts.map(({ id, required, have, canAfford }) => (
                        <div key={id} className={`wp-cost-item ${canAfford ? 'ok' : 'need'}`}>
                          <ResourceChip
                            resourceId={id}
                            amount={required}
                            size="small"
                            canAfford={canAfford}
                            showTooltip={true}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Building Requirements */}
                    {buildingCosts.length > 0 && (
                      <div className="wp-card-buildings">
                        <div className="wp-card-section-sublabel">🏗️ Buildings Consumed</div>
                        <div className="wp-card-building-list">
                          {buildingCosts.map(({ id, required, have, canAfford }) => (
                            <div key={id} className={`wp-building-item ${canAfford ? 'ok' : 'need'}`}>
                              <BuildingChip id={id} size="small" showName={false} />
                              <span className="wp-building-count">{have}/{required}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Lore/Flavor - always visible */}
                {(wonder.lore || wonder.flavorText) && (
                  <div className="wp-card-lore">
                    {wonder.flavorText && <p className="wp-lore-flavor">"{wonder.flavorText}"</p>}
                    {wonder.lore && <p className="wp-lore-text">{wonder.lore}</p>}
                  </div>
                )}

                {/* Action Button */}
                <div className="wp-card-action">
                  {isBuilt ? (
                    <div className="wp-action-built">✅ Wonder Active</div>
                  ) : isBuilding ? (
                    <div className="wp-action-building">⏳ Under Construction</div>
                  ) : currentWonderConstruction ? (
                    <button className="wp-action-btn disabled" disabled>Busy Building</button>
                  ) : wondersRemaining <= 0 ? (
                    <button className="wp-action-btn disabled" disabled>Max Wonders</button>
                  ) : !siteDiscovered ? (
                    <button className="wp-action-btn locked site-locked" disabled>🗺️ Discover Site First</button>
                  ) : (
                    <button
                      className={`wp-action-btn ${buildCheck.canBuild ? 'can-build' : 'locked'}`}
                      disabled={!buildCheck.canBuild}
                      onClick={() => handleStartConstruction(wonder.id)}
                    >
                      {buildCheck.canBuild ? '🏛️ Build Wonder' :
                       !allResourcesOk && !allBuildingsOk ? '🔒 Need Resources & Buildings' :
                       !allResourcesOk ? '💰 Need Resources' :
                       !allBuildingsOk ? '🏗️ Need Buildings' : '🔒 Locked'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
        </>
      )}

      {/* === GREAT WORKS TAB === */}
      {activeTab === 'greatWorks' && (
        <>
          {/* System Info Header */}
          <div className="wp-header">
            <div className="wp-header-left">
              <h3>🗿 Great Works</h3>
              <p className="wp-subtitle">Map-bound monuments requiring tile clusters</p>
            </div>
            <div className="wp-slots">
              <div className="wp-slots-pips">
                {[...Array(GREAT_WORKS_BALANCE.maxTotal)].map((_, i) => (
                  <div
                    key={i}
                    className={`wp-slot-pip ${i < greatWorksBuilt ? 'filled' : ''}`}
                    title={i < greatWorksBuilt ? GREAT_WORKS[builtGreatWorks[i]]?.name : 'Empty'}
                  >
                    {i < greatWorksBuilt && GREAT_WORKS[builtGreatWorks[i]]?.icon}
                  </div>
                ))}
              </div>
              <span className="wp-slots-text">{greatWorksBuilt}/{GREAT_WORKS_BALANCE.maxTotal} Slots</span>
            </div>
          </div>

          {/* How It Works */}
          <div className="wp-info-bar">
            <div className="wp-info-item">
              <span className="wp-info-icon">🗺️</span>
              <span>Place on world map</span>
            </div>
            <div className="wp-info-item">
              <span className="wp-info-icon">🔷</span>
              <span>Requires tile clusters</span>
            </div>
            <div className="wp-info-item">
              <span className="wp-info-icon">⏳</span>
              <span>One at a time</span>
            </div>
            <div className="wp-info-item">
              <span className="wp-info-icon">💰</span>
              <span>Cancel = 50% refund</span>
            </div>
          </div>

          <div className="wp-content">
            {/* Active Great Work Construction */}
            {currentGreatWorkConstruction && gwProgressInfo && (
              <div className="wp-construction gw-construction">
                <div className="wp-con-header">
                  <span className="wp-con-icon">{gwProgressInfo.work.icon}</span>
                  <div className="wp-con-info">
                    <span className="wp-con-name">{gwProgressInfo.work.name}</span>
                    <span className="wp-con-time">{gwProgressInfo.formattedRemaining} remaining</span>
                  </div>
                  {GREAT_WORKS_BALANCE.canCancel && !showGWConfirmCancel && (
                    <button
                      className="wp-con-cancel"
                      onClick={() => setShowGWConfirmCancel(true)}
                    >
                      Cancel
                    </button>
                  )}
                </div>
                <div className="wp-con-bar gw-bar">
                  <div
                    className="wp-con-fill gw-fill"
                    style={{ width: `${gwProgressInfo.percent}%` }}
                  />
                  <span className="wp-con-percent">{Math.floor(gwProgressInfo.percent)}%</span>
                </div>
                {showGWConfirmCancel && (
                  <div className="wp-con-confirm">
                    <span>Cancel construction? 50% resources refunded.</span>
                    <div className="wp-con-confirm-btns">
                      <button className="btn-confirm" onClick={handleGWCancel}>Yes, Cancel</button>
                      <button className="btn-deny" onClick={() => setShowGWConfirmCancel(false)}>No</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Built Great Works */}
            {builtGreatWorks?.length > 0 && (
              <div className="wp-built gw-built">
                <div className="wp-built-header">
                  <span>✨ Your Great Works</span>
                </div>
                <div className="wp-built-grid">
                  {builtGreatWorks.map(workId => {
                    const work = GREAT_WORKS[workId];
                    if (!work) return null;
                    const isDestroying = showGWConfirmDestroy === workId;
                    const location = greatWorkLocations?.[workId];

                    return (
                      <div key={workId} className={`wp-built-card gw-card ${isDestroying ? 'destroying' : ''}`}>
                        <div className="wp-built-main">
                          <span className="wp-built-icon">{work.icon}</span>
                          <div className="wp-built-info">
                            <span className="wp-built-name">{work.name}</span>
                            {location && (
                              <span className="wp-built-location">📍 {location}</span>
                            )}
                            <div className="wp-built-effects">
                              {work.effects.productionBonus && Object.entries(work.effects.productionBonus).map(([res, mult]) => (
                                <span key={res} className="wp-effect-mini bonus">
                                  {resources[res]?.icon}+{Math.round(mult * 100)}%
                                </span>
                              ))}
                              {work.effects.globalProductionBonus && (
                                <span className="wp-effect-mini global">
                                  🌍+{Math.round(work.effects.globalProductionBonus * 100)}%
                                </span>
                              )}
                              {work.effects.flatBonus && Object.entries(work.effects.flatBonus).map(([res, amt]) => (
                                <span key={res} className="wp-effect-mini flat">
                                  {resources[res]?.icon}+{amt}/s
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {isDestroying ? (
                          <div className="wp-built-destroy-confirm">
                            <span className="destroy-warn">⚠️ No refund!</span>
                            <button className="btn-destroy" onClick={() => handleGWDestroy(workId)}>Destroy</button>
                            <button className="btn-keep" onClick={() => setShowGWConfirmDestroy(null)}>Keep</button>
                          </div>
                        ) : (
                          <button
                            className="wp-built-destroy"
                            onClick={() => setShowGWConfirmDestroy(workId)}
                            disabled={currentGreatWorkConstruction !== null}
                            title="Destroy Great Work"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
                {greatWorksRemaining === 0 && !currentGreatWorkConstruction && (
                  <div className="wp-max-warning">
                    ⚠️ All slots full. Destroy a Great Work to build another.
                  </div>
                )}
              </div>
            )}

            {/* Tier Selector */}
            <div className="wp-tier-selector gw-tier-selector">
              {gwTiers.map(tier => {
                const tierList = getGreatWorksByTier(tier);
                const builtInTier = tierList.filter(w => builtGreatWorks?.includes(w.id)).length;
                return (
                  <button
                    key={tier}
                    className={`wp-tier-btn ${selectedGWTier === tier ? 'active' : ''}`}
                    onClick={() => setSelectedGWTier(tier)}
                  >
                    <span className="wp-tier-icon">{GREAT_WORKS_BALANCE.tierNames?.[tier]?.[0] || '🗿'}</span>
                    <span className="wp-tier-name">T{tier}</span>
                    {builtInTier > 0 && <span className="wp-tier-check">✓</span>}
                  </button>
                );
              })}
            </div>

            {/* Tier Info */}
            <div className="wp-era-info gw-era-info">
              <span className="wp-era-icon">🗿</span>
              <span className="wp-era-name">{GREAT_WORKS_BALANCE.tierNames?.[selectedGWTier] || `Tier ${selectedGWTier}`}</span>
              <span className="wp-era-desc">Map-bound wonders</span>
            </div>

            {/* Great Works Cards Grid */}
            <div className="wp-grid gw-grid" key={`gw-tier-${selectedGWTier}`}>
              {gwTierWorks.map(work => {
                const isBuilt = builtGreatWorks?.includes(work.id);
                const isBuilding = currentGreatWorkConstruction === work.id;
                const affordCheck = canAffordGreatWork(work.id, playerResources);
                const reqSummary = getRequirementSummary(work.id);
                const validPlacements = validPlacementsMap[work.id] || [];
                const hasValidLocation = validPlacements.length > 0;

                const resourceCosts = Object.entries(work.cost.resources || {}).map(([res, amt]) => ({
                  id: res,
                  required: amt,
                  have: playerResources[res] || 0,
                  canAfford: (playerResources[res] || 0) >= amt,
                }));

                const canBuild = affordCheck.canAfford && hasValidLocation && !currentGreatWorkConstruction && greatWorksRemaining > 0;

                return (
                  <div
                    key={work.id}
                    className={`wp-card gw-card ${isBuilt ? 'built' : ''} ${isBuilding ? 'building' : ''}`}
                  >
                    {/* Card Header */}
                    <div className="wp-card-header">
                      <span className="wp-card-icon">{work.icon}</span>
                      <div className="wp-card-title">
                        <span className="wp-card-name">{work.name}</span>
                        <span className="wp-card-time">⏱️ {formatTime(work.buildTime)}</span>
                      </div>
                      {isBuilt && <span className="wp-card-badge built">✅ Built</span>}
                      {isBuilding && <span className="wp-card-badge building">⏳ Building</span>}
                    </div>

                    {/* Description */}
                    <div className="wp-card-desc">{work.description}</div>

                    {/* Map Requirements */}
                    {!isBuilt && reqSummary && (
                      <div className="wp-card-section gw-requirements">
                        <div className="wp-card-section-label">🗺️ Map Requirements</div>
                        <div className="gw-req-list">
                          {reqSummary.anchor && (
                            <div className="gw-req-item">
                              <span className="gw-req-icon">📍</span>
                              <span>{reqSummary.anchor}</span>
                            </div>
                          )}
                          {reqSummary.nearby && (
                            <div className="gw-req-item">
                              <span className="gw-req-icon">🔷</span>
                              <span>{reqSummary.nearby}</span>
                            </div>
                          )}
                          <div className={`gw-req-status ${hasValidLocation ? 'ok' : 'need'}`}>
                            {hasValidLocation
                              ? `✓ ${validPlacements.length} valid location${validPlacements.length > 1 ? 's' : ''}`
                              : '✗ No valid locations - explore more!'}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Effects Grid */}
                    <div className="wp-card-section">
                      <div className="wp-card-section-label">Effects</div>
                      <div className="wp-card-effects">
                        {work.effects.productionBonus && Object.entries(work.effects.productionBonus).map(([res, mult]) => (
                          <div key={res} className="wp-effect bonus">
                            <span className="wp-effect-icon">{resources[res]?.icon || '📦'}</span>
                            <span className="wp-effect-text">+{Math.round(mult * 100)}% {resources[res]?.name}</span>
                          </div>
                        ))}
                        {work.effects.globalProductionBonus && (
                          <div className="wp-effect global">
                            <span className="wp-effect-icon">🌍</span>
                            <span className="wp-effect-text">+{Math.round(work.effects.globalProductionBonus * 100)}% ALL production</span>
                          </div>
                        )}
                        {work.effects.researchSpeedBonus && (
                          <div className="wp-effect research">
                            <span className="wp-effect-icon">🔬</span>
                            <span className="wp-effect-text">+{Math.round(work.effects.researchSpeedBonus * 100)}% research speed</span>
                          </div>
                        )}
                        {work.effects.flatBonus && Object.entries(work.effects.flatBonus).map(([res, amt]) => (
                          <div key={res} className="wp-effect flat">
                            <span className="wp-effect-icon">{resources[res]?.icon || '📦'}</span>
                            <span className="wp-effect-text">+{amt}/sec {resources[res]?.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Cost Section (only if not built) */}
                    {!isBuilt && (
                      <div className="wp-card-section">
                        <div className="wp-card-section-label">Cost</div>
                        <div className="wp-card-costs">
                          {resourceCosts.map(({ id, required, have, canAfford: canAff }) => (
                            <div key={id} className={`wp-cost-item ${canAff ? 'ok' : 'need'}`}>
                              <ResourceChip
                                resourceId={id}
                                amount={required}
                                size="small"
                                canAfford={canAff}
                                showTooltip={true}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Lore/Flavor */}
                    {(work.lore || work.flavorText) && (
                      <div className="wp-card-lore">
                        {work.flavorText && <p className="wp-lore-flavor">"{work.flavorText}"</p>}
                        {work.lore && <p className="wp-lore-text">{work.lore}</p>}
                      </div>
                    )}

                    {/* Action Button */}
                    <div className="wp-card-action">
                      {isBuilt ? (
                        <div className="wp-action-built">✅ Great Work Active</div>
                      ) : isBuilding ? (
                        <div className="wp-action-building">⏳ Under Construction</div>
                      ) : currentGreatWorkConstruction ? (
                        <button className="wp-action-btn disabled" disabled>Busy Building</button>
                      ) : greatWorksRemaining <= 0 ? (
                        <button className="wp-action-btn disabled" disabled>Max Great Works</button>
                      ) : !hasValidLocation ? (
                        <button className="wp-action-btn disabled" disabled>🗺️ No Valid Tiles</button>
                      ) : (
                        <button
                          className={`wp-action-btn ${canBuild ? 'can-build' : 'locked'}`}
                          disabled={!canBuild}
                          onClick={() => handleSelectGreatWork(work.id)}
                        >
                          {canBuild ? '🗿 Select Location' : '💰 Need Resources'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default MonumentsPanel;
